import { NgClass, NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, forwardRef, signal } from '@angular/core';
import { NodeViewComponent } from '@examdojo/core/tiptap';
import { SuperLatexComponent, SuperLatexContent, SuperLatexEditorComponent } from '@examdojo/super-latex';
import { v4 as uuid } from 'uuid';
import { SuperLatexBlock } from '../../../model';
import { DragWithHandleOnlyDirective } from '../drag-with-handle-only.directive';
import { DEFAULT_SUPER_LATEX_PROPS } from './super-latex.model';

@Component({
  selector: 'dojo-super-latex-node',
  standalone: true,
  imports: [NgClass, NgStyle, SuperLatexComponent, SuperLatexEditorComponent],
  templateUrl: './super-latex.component.html',
  styles: `
    :host {
      @apply block my-4 text-lg;

      &.ProseMirror-selectednode {
        background-color: transparent !important;
        outline: none !important;
      }
    }

    .super-latex-wrapper {
      position: relative;

      &:hover .edit-btn {
        opacity: 1;
      }
    }

    .edit-btn {
      position: absolute;
      top: 4px;
      right: 4px;
      padding: 6px;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.15s, background-color 0.15s;
      color: #6b7280;
      z-index: 10;

      &:hover {
        background: #f3f4f6;
        color: #374151;
      }
    }
  `,
  hostDirectives: [DragWithHandleOnlyDirective],
  providers: [
    {
      provide: NodeViewComponent,
      useExisting: forwardRef(() => SuperLatexNodeComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuperLatexNodeComponent extends NodeViewComponent<SuperLatexBlock> {
  readonly latexNodes = this.getProp('latex_nodes');
  readonly annotations = this.getProp('annotations');
  readonly connections = this.getProp('connections');
  readonly layout = this.getProp('layout');
  readonly highlightedAnchors = this.getProp('highlighted_anchors');
  readonly customCss = this.getProp('custom_css');
  readonly customStyle = this.getProp('custom_style');

  readonly showEditor = signal(false);

  readonly content = computed<SuperLatexContent>(() => ({
    latex_nodes: this.latexNodes(),
    annotations: this.annotations() ?? undefined,
    connections: this.connections() ?? undefined,
    layout: this.layout() ?? undefined,
    highlighted_anchors: this.highlightedAnchors() ?? undefined,
  }));

  override getDefaults = (): SuperLatexBlock => ({
    ...DEFAULT_SUPER_LATEX_PROPS,
    id: uuid(),
  });

  openEditor(): void {
    this.showEditor.set(true);
  }

  closeEditor(): void {
    this.showEditor.set(false);
  }

  onContentChange(updatedContent: SuperLatexContent): void {
    this.updateProps({
      latex_nodes: updatedContent.latex_nodes,
      annotations: updatedContent.annotations,
      connections: updatedContent.connections,
      layout: updatedContent.layout,
      highlighted_anchors: updatedContent.highlighted_anchors,
    });
  }
}
